<template>
  <a-input
    v-model:value="displayValue"
    :placeholder="placeholder"
    :disabled="disabled"
    :size="size"
    :allow-clear="allowClear"
    :maxlength="12"
    :status="computedStatus"
    class="ant-license-plate-input"
    @update:value="onInput"
    @blur="onBlur"
    @pressEnter="onPressEnter"
  >
    <template #prefix>
      <i class="fas fa-car" />
    </template>
  </a-input>
</template>

<script lang="ts" setup>
import { computed, ref, watch } from "vue";
import type { SizeType } from "ant-design-vue/es/config-provider";

interface Props {
  modelValue?: string;
  placeholder?: string;
  disabled?: boolean;
  size?: SizeType;
  allowClear?: boolean;
  status?: "" | "error" | "warning";
  validateOnBlur?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: "",
  placeholder: "VD: 30A-123.45",
  disabled: false,
  size: "middle",
  allowClear: true,
  status: "",
  validateOnBlur: true,
});

const emit = defineEmits<{
  (e: "update:modelValue", value: string): void;
  (e: "change", value: string): void;
  (e: "blur", event: FocusEvent): void;
  (e: "pressEnter", event: KeyboardEvent): void;
  (e: "validate", valid: boolean): void;
}>();

const displayValue = ref("");

/**
 * Chuẩn hóa biển số VN:
 * - 30A12345 / 30A-12345 / 30A-123.45 / 51F1-234.56
 */
const sanitize = (value: string) =>
  value
    .toUpperCase()
    .replace(/[^A-Z0-9.\-]/g, "")
    .slice(0, 12);

const formatPlate = (raw: string) => {
  const clean = raw.replace(/[^A-Z0-9]/g, "");
  if (clean.length <= 2) return clean;
  if (clean.length <= 3) return `${clean.slice(0, 2)}${clean.slice(2)}`;

  // series: digits + letters (2-4 chars after province)
  const match = clean.match(/^(\d{2})([A-Z]{1,2}\d?)(\d{0,5})$/);
  if (!match) return clean;

  const [, province, series, numbers] = match;
  if (!numbers) return `${province}${series}`;

  if (numbers.length <= 3) {
    return `${province}${series}-${numbers}`;
  }
  return `${province}${series}-${numbers.slice(0, 3)}.${numbers.slice(3)}`;
};

const isValidPlate = (value: string) => {
  if (!value) return true;
  // 30A-12345 | 30A-123.45 | 51F1-234.56
  return /^\d{2}[A-Z]{1,2}\d?(?:-\d{3}(?:\.\d{1,2})?|-\d{4,5})$/.test(value);
};

watch(
  () => props.modelValue,
  (val) => {
    displayValue.value = formatPlate(sanitize(val ?? ""));
  },
  { immediate: true }
);

const localError = computed(() => {
  if (!props.validateOnBlur) return false;
  return !!props.modelValue && !isValidPlate(props.modelValue);
});

const computedStatus = computed(() => props.status || (localError.value ? "error" : ""));

const onInput = (val: string) => {
  const formatted = formatPlate(sanitize(val));
  displayValue.value = formatted;
  emit("update:modelValue", formatted);
  emit("change", formatted);
};

const onBlur = (e: FocusEvent) => {
  emit("validate", isValidPlate(props.modelValue ?? ""));
  emit("blur", e);
};

const onPressEnter = (e: KeyboardEvent) => emit("pressEnter", e);
</script>

<!--
  Hướng dẫn sử dụng — Biển số xe
  Global: <h-licensePlate v-model="plate" />
  Import: import { LicensePlateInput } from '@/components/ant/input'

  Ví dụ: 30A-123.45, 51F-12345
  Props: modelValue, validateOnBlur...
  Events: update:modelValue, change, blur, pressEnter, validate
  Docs: src/components/ant/input/README.md
-->
